import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from '../config/axiosConfig';
import ReactMarkdown from 'react-markdown';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Button,
  useTheme
} from '@mui/material';
import {
  AccessTime as AccessTimeIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import logo from '../images/rodeoLogo.png';
import { Helmet } from 'react-helmet-async';

const BlogDetail = () => {
  const { id } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();

  useEffect(() => {
    axios.get(`/blogs/${id}`)
      .then(response => {
        setBlog(response.data);
        setLoading(false);
      })
      .catch(error => {
        console.error("Error fetching blog:", error);
        if (error.response && error.response.status === 404) {
          setBlog(null);
        }
        setLoading(false);
      });
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!blog) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h5">Blog post not found</Typography>
        <Button component={Link} to="/blogs" variant="contained" sx={{ mt: 2 }}>
          Return to Blog List
        </Button>
      </Box>
    );
  }

  const renderContentBlock = (block, index) => {
    switch (block.type) {
      case 'text':
        return (
          <Box key={index} sx={{ mb: 3 }}>
            <ReactMarkdown
              components={{
                h1: (props) => <Typography variant="h4" component="h2" sx={{ mt: 4, mb: 2, fontWeight: 'bold' }} {...props} />,
                h2: (props) => <Typography variant="h5" component="h3" sx={{ mt: 4, mb: 2, fontWeight: 'bold' }} {...props} />,
                h3: (props) => <Typography variant="h6" component="h4" sx={{ mt: 3, mb: 2, fontWeight: 'bold' }} {...props} />,
                p: (props) => <Typography paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8, mb: 2 }} {...props} />,
                ul: (props) => <Box component="ul" sx={{ pl: 4, mb: 2 }} {...props} />,
                ol: (props) => <Box component="ol" sx={{ pl: 4, mb: 2 }} {...props} />,
                li: (props) => <Typography component="li" sx={{ fontSize: '1.1rem', lineHeight: 1.8, mb: 1 }} {...props} />
              }}
            >
              {block.text}
            </ReactMarkdown>
          </Box>
        );
      case 'image':
        return (
          <Box
            key={index}
            component="img"
            src={`${block.src}`}
            alt={block.alt || 'Blog image'}
            sx={{
              maxWidth: '100%',
              height: 'auto',
              display: 'block',
              my: 3,
              borderRadius: '4px',
              boxShadow: theme.shadows[2]
            }}
          />
        );
      case 'video':
        return (
          <Box
            key={index}
            sx={{
              position: 'relative',
              paddingBottom: '56.25%',
              height: 0,
              overflow: 'hidden',
              width: '100%',
              my: 3
            }}
          >
            <iframe
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                borderRadius: '4px'
              }}
              src={block.src}
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </Box>
        );
      default:
        console.warn(`Unknown content type: ${block.type}`);
        return null;
    }
  };

  return (
    <Box>
      <Helmet>
        <title>{blog.title} | Market Rodeo</title>
        <meta name="description" content={blog.summary || blog.description || blog.title} />
        <meta property="og:title" content={blog.title} />
        <meta property="og:description" content={blog.summary || blog.description || blog.title} />
      </Helmet>
      {/* Clean header — no banner image */}
      <Container maxWidth="md" sx={{ pt: 4, pb: 2 }}>
        <Button
          component={Link}
          to="/blogs"
          startIcon={<ArrowBackIcon />}
          sx={{
            mb: 3,
            color: 'text.secondary',
            '&:hover': { color: 'text.primary' }
          }}
        >
          Back to Blogs
        </Button>

        {/* Logo + Title header */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2.5, mb: 3 }}>
          <Box
            component="img"
            src={logo}
            alt="Market Rodeo"
            sx={{
              width: 48,
              height: 48,
              borderRadius: '8px',
              flexShrink: 0,
              mt: 0.5
            }}
          />
          <Box>
            <Typography
              variant="h3" component="h1"
              sx={{
                fontWeight: 'bold',
                fontSize: { xs: '1.75rem', md: '2.5rem' },
                lineHeight: 1.2,
                mb: 2
              }}
            >
              {blog.title}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 2, md: 3 }, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PersonIcon sx={{ fontSize: 18 }} color="action" />
                <Typography variant="body2" color="text.secondary">{blog.author}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CalendarIcon sx={{ fontSize: 18 }} color="action" />
                <Typography variant="body2" color="text.secondary">
                  {new Date(blog.publishedDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AccessTimeIcon sx={{ fontSize: 18 }} color="action" />
                <Typography variant="body2" color="text.secondary">{blog.readTime} min read</Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Container>

      {/* Blog content */}
      <Container maxWidth="md" sx={{ pb: 8 }}>
        {Array.isArray(blog.content) ? (
          blog.content.map(renderContentBlock)
        ) : (
          <Typography color="error">Error: Blog content is not formatted correctly.</Typography>
        )}
      </Container>
    </Box>
  );
};

export default BlogDetail;